import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { format, isAfter, parseISO } from "date-fns";

import { Button, Col, Row, ThemeProvider } from "react-bootstrap";
import uuid from "react-uuid";

import { axiosGetRequest } from "../../api/ApiClient";
import { PenToSquareRegularIcon } from "../../components/Icons";
import PeriodoPlanoAtividadeDTO from "../../model/dto/PeriodoPlanoAtividadeDTO";
import ModalCallback from "../../model/ModalCallback";
import PaginationBaseModel from "../../model/PaginationBaseModel";
import ButtonStyles from "../../styles/components/ButtonStyles";
import TableStyles from "../../styles/components/TableStyles";
import {
    StyledBody,
    StyledFormCheck,
    StyledHeader,
    StyledTitle,
} from "../../styles/MainStyles";
import { MASK_DATE, MASK_DATE_BR } from "../../utils/constants";
import GenericUtils from "../../utils/GenericUtils";
import StringUtils from "../../utils/types/StringUtils";
import ModalLockPeriod from "./ModalLockPeriod";
import ModalValidityPeriod from "./ModalValidityPeriod";

function ParametrizeActivityPlan() {
    const theme = {};

    const { instance } = useMsal();
    const acessToken = GenericUtils.getAccessTokenDocente();

    const translate = {
        tableEmptyText: StringUtils.translate("tableEmpty"),
    };

    const pageItem = "page-item";
    const active = "active";

    // Default states
    const [activityPlanList, setActivityPlanList] = useState(
        new Array<PeriodoPlanoAtividadeDTO>()
    );
    // Modal
    const [openModalValidityPeriod, setOpenModalValidityPeriod] =
        useState(false);
    const [paramsModalValidityPeriod, setParamsModalValidityPeriod] =
        useState(null);
    const [openModalLockPeriod, setOpenModalLockPeriod] = useState(false);
    const [paramsModalLockPeriod, setParamsModalLockPeriod] = useState(null);
    // Pagination states
    const [itensPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [firstPage, setFirstPage] = useState(0);
    const [secondPage, setSecondPage] = useState(0);
    const [thirdPage, setThirdPage] = useState(0);
    const [lastPage, setLastPage] = useState(0);
    const [isAll, setIsAll] = useState(false);
    const [, setSeachParams] = useState<PaginationBaseModel>();
    const today = new Date();

    function clearPagination(response?: any) {
        setSeachParams(undefined);
        setTotalRecords(response?.data?.length || 0);
        setCurrentPage(0);
        setFirstPage(0);
        setSecondPage(0);
        setThirdPage(0);
        setLastPage(0);
    }

    function updatePagination(response: any, offset: number) {
        const tempCurrentPage = offset;
        const tempTotalRecords = response.headers["content-range"].split("/")[1]
            ? Number(response.headers["content-range"].split("/")[1])
            : 0;
        const tempLastPage = Math.ceil(tempTotalRecords / itensPerPage) - 1;
        setCurrentPage(tempCurrentPage);
        setTotalRecords(tempTotalRecords);
        setLastPage(tempLastPage);
        if (tempLastPage - tempCurrentPage === tempLastPage) {
            setFirstPage(tempCurrentPage);
            setSecondPage(tempCurrentPage + 1);
            setThirdPage(tempCurrentPage + 2);
        } else if (
            tempLastPage - tempCurrentPage < tempLastPage &&
            tempLastPage - tempCurrentPage > 0
        ) {
            setFirstPage(tempCurrentPage - 1);
            setSecondPage(tempCurrentPage);
            setThirdPage(tempCurrentPage + 1);
        } else if (tempLastPage - tempCurrentPage === 0) {
            setFirstPage(tempCurrentPage - 2);
            setSecondPage(tempCurrentPage - 1);
            setThirdPage(tempCurrentPage);
        }
    }

    async function getActivityPlan(dto?: PaginationBaseModel) {
        const response = await axiosGetRequest(
            `/docente/v1/periodos-plano-atividade${StringUtils.getQueryParams(
                dto
            )}`,
            acessToken,
            instance
        );
        if (response.status === 206 && response?.data?._result) {
            const responseList = response.data._result.map(
                (item: PeriodoPlanoAtividadeDTO) => {
                    const itemResp: PeriodoPlanoAtividadeDTO = {
                        id: item.id,
                        ano: item.ano,
                        semestre: item.semestre,
                        dtInicio: item.dtInicio,
                        dtFim: item.dtFim,
                        tipoPeriodo: item.tipoPeriodo,
                    };
                    return itemResp;
                }
            );
            setActivityPlanList(responseList);
            updatePagination(response, Number(dto?._offset) || 0);
        } else if (response.status === 200 && response.data) {
            const responseList = response.data.map(
                (item: PeriodoPlanoAtividadeDTO) => {
                    const itemResp: PeriodoPlanoAtividadeDTO = {
                        id: item.id,
                        ano: item.ano,
                        semestre: item.semestre,
                        dtInicio: item.dtInicio,
                        dtFim: item.dtFim,
                        tipoPeriodo: item.tipoPeriodo,
                    };
                    return itemResp;
                }
            );
            setActivityPlanList(responseList);
            clearPagination(response);
        } else {
            setActivityPlanList(new Array<PeriodoPlanoAtividadeDTO>());
            clearPagination();
        }
    }

    function handleSearch() {
        const dto: PaginationBaseModel = {
            _limit: String(itensPerPage),
            asc: "false",
            desc: "true",
            _offset: "0",
            listar_todos: isAll,
        };
        setSeachParams(dto);
        getActivityPlan(dto);
    }

    function handlePaginate(page: string) {
        const dto: PaginationBaseModel = {
            _limit: String(itensPerPage),
            asc: "false",
            desc: "true",
            _offset: page || String(currentPage),
            listar_todos: isAll,
        };
        setSeachParams(dto);
        getActivityPlan(dto);
    }

    const handleEdit = (param: any) => {
        if (param.semestre) {
            setParamsModalValidityPeriod(param);
            setOpenModalValidityPeriod(true);
        } else {
            setParamsModalLockPeriod(param);
            setOpenModalLockPeriod(true);
        }
    };

    const handleCloseModalValidityPeriod = (callback?: ModalCallback) => {
        setOpenModalValidityPeriod(false);
        setParamsModalValidityPeriod(null);
        if (callback?.success) handleSearch();
    };

    const handleCloseModalLockPeriod = (callback?: ModalCallback) => {
        setOpenModalLockPeriod(false);
        setParamsModalLockPeriod(null);
        if (callback?.success) handleSearch();
    };

    const isAfterCurrentDate = (date: string): boolean => {
        return isAfter(parseISO(format(new Date(), MASK_DATE)), parseISO(date));
    };

    useEffect(() => {
        handleSearch();
    }, [isAll]);

    const firstPageView =
        firstPage >= 0 ? (
            <li
                className={
                    currentPage === firstPage
                        ? `${pageItem} ${active}`
                        : `${pageItem}`
                }
            >
                <Button
                    className="page-link"
                    href="#"
                    onClick={() => handlePaginate(String(firstPage))}
                >
                    {firstPage + 1}
                </Button>
            </li>
        ) : null;

    const secondPageView = (
        <li
            className={
                currentPage === secondPage
                    ? `${pageItem} ${active}`
                    : `${pageItem}`
            }
        >
            <Button
                className="page-link"
                href="#"
                onClick={() => handlePaginate(String(secondPage))}
            >
                {secondPage + 1}
            </Button>
        </li>
    );

    const thirdPageView =
        thirdPage <= lastPage ? (
            <li
                className={
                    currentPage === thirdPage
                        ? `${pageItem} ${active}`
                        : `${pageItem}`
                }
            >
                <Button
                    className="page-link"
                    href="#"
                    onClick={() => handlePaginate(String(thirdPage))}
                >
                    {thirdPage + 1}
                </Button>
            </li>
        ) : null;

    const handleCheckAll = () => {
        setIsAll(!isAll);
    };

    const formattedDate = (date: string) => {
        return StringUtils.formatDateString(date, MASK_DATE_BR);
    };

    const hasToDisabled = (initialDate: string, finalDate: string): boolean => {
        if (!initialDate || !finalDate) return false;

        const initial = new Date(initialDate);
        const final = new Date(finalDate);

        return today >= initial && today <= final;
    };

    const bodyOfTable = activityPlanList.map((item) => {
        const isDisabled = hasToDisabled(item.dtInicio, item.dtFim);

        return (
            <tr key={uuid()}>
                <td>{item.tipoPeriodo}</td>
                <td>
                    {`${formattedDate(item.dtInicio)} - ${formattedDate(
                        item.dtFim
                    )}`}
                    {!isAfterCurrentDate(item.dtFim) && (
                        <ButtonStyles.BtnTable
                            className="btn-align space-left"
                            variant="link"
                            disabled={isDisabled}
                            onClick={() => {
                                handleEdit(item);
                            }}
                        >
                            <PenToSquareRegularIcon
                                style={{
                                    width: 16,
                                    fill: isDisabled ? "gray" : "black",
                                }}
                            />
                        </ButtonStyles.BtnTable>
                    )}
                </td>
            </tr>
        );
    });

    return (
        <ThemeProvider theme={theme}>
            <StyledHeader>
                <StyledTitle>
                    Parametrizar TACH (Termo de Acordo de Carga Horária)
                </StyledTitle>
            </StyledHeader>
            <StyledBody>
                <Row className="gap-1">
                    <Col xs="auto" md="auto" lg="auto">
                        <ButtonStyles.BtnPrimary
                            style={{ width: 184 }}
                            onClick={() => {
                                setOpenModalValidityPeriod(true);
                            }}
                        >
                            Período de vigência
                        </ButtonStyles.BtnPrimary>
                    </Col>
                    <Col xs="auto" md="auto" lg="auto">
                        <ButtonStyles.BtnOutlinePrimary
                            style={{ width: 184 }}
                            onClick={() => {
                                setOpenModalLockPeriod(true);
                            }}
                        >
                            Período de bloqueio
                        </ButtonStyles.BtnOutlinePrimary>
                    </Col>
                    <Col>
                        <div
                            style={{
                                display: "flex",
                                height: 50,
                                alignItems: "center",
                            }}
                        >
                            <StyledFormCheck
                                type="checkbox"
                                checked={isAll}
                                label="Listar todos os períodos"
                                onChange={handleCheckAll}
                            />
                        </div>
                    </Col>
                </Row>
                <Row className="mt-5">
                    <Col>
                        <TableStyles.TitleTable>
                            Períodos cadastrados
                        </TableStyles.TitleTable>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <TableStyles.TablePrimary bordered striped responsive>
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Período</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activityPlanList?.length === 0 ? (
                                    <tr key={uuid()} className="table-empty">
                                        <td colSpan={7}>
                                            {translate.tableEmptyText}
                                        </td>
                                    </tr>
                                ) : (
                                    bodyOfTable
                                )}
                            </tbody>
                        </TableStyles.TablePrimary>
                    </Col>
                </Row>
                {totalRecords > itensPerPage ? (
                    <nav aria-label="Page navigation example">
                        <ul className="pagination justify-content-end">
                            {currentPage > firstPage ? (
                                <li className="page-item">
                                    <Button
                                        className="page-link"
                                        href="#"
                                        onClick={() =>
                                            handlePaginate(
                                                String(currentPage - 1)
                                            )
                                        }
                                    >
                                        Anterior
                                    </Button>
                                </li>
                            ) : null}
                            {firstPageView}
                            {secondPageView}
                            {thirdPageView}
                            {currentPage < lastPage ? (
                                <li className="page-item">
                                    <Button
                                        className="page-link"
                                        href="#"
                                        onClick={() =>
                                            handlePaginate(
                                                String(currentPage + 1)
                                            )
                                        }
                                    >
                                        Próximo
                                    </Button>
                                </li>
                            ) : null}
                        </ul>
                    </nav>
                ) : null}
                <ModalValidityPeriod
                    open={openModalValidityPeriod}
                    params={paramsModalValidityPeriod}
                    onClose={handleCloseModalValidityPeriod}
                />
                <ModalLockPeriod
                    open={openModalLockPeriod}
                    params={paramsModalLockPeriod}
                    onClose={handleCloseModalLockPeriod}
                />
            </StyledBody>
        </ThemeProvider>
    );
}

export default ParametrizeActivityPlan;
